<script setup lang="ts">
import { DefinitionDto, NumericQuantityDto } from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import ValidationIndicator from '@/components/forms/ValidationIndicator/ValidationIndicator.vue';
import { edit as editIcon } from '@/components/icons/icons.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { isPredefinedEntity, translateEntityProperty } from '@/utils/helper.ts';
import DefinitionModal from '@/views/quantities/components/DefinitionModal.vue';
import DefinitionTable from '@/views/quantities/components/DefinitionTable.vue';
import DefinitionParameterSets from '@/views/quantities/components/useInEvaluations/DefinitionParameterSets.vue';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { cloneDeep } from 'lodash';
import { NIL } from 'uuid';
import { useFieldValue, useFormValues } from 'vee-validate';
import { computed, ref, toRef, watchEffect } from 'vue';

const { t } = useTranslation();

type DefinitionProps = {
  name: string;
  disabled?: boolean;
  allowExtend: boolean;
};
const props = defineProps<DefinitionProps>();

type DefinitionEmits = {
  update: [DefinitionDto];
  remove: [];
  'click:edit': [];
};
type Quantity = NumericQuantityDto;

const emit = defineEmits<DefinitionEmits>();

const mode = useFormMode();

const { value: formValues } = useFormValues<Quantity>();
const isReferenced = computed(() => formValues.isReferenced ?? false);

const definition = useFieldValue<DefinitionDto>(toRef(() => props.name));
const isPredefined = computed(() => isPredefinedEntity(definition.value));
const internalDefinition = ref<DefinitionDto>(cloneDeep(definition.value));
const isNewDefinition = computed(() => definition.value.id === NIL);

const [isEditDefinitionDialogOpen, toggleEditDefinitionDialog] = useToggle(false);

const hasDependingParameterSets = computed(() => {
  if (!internalDefinition?.value?.definitionParameterValueSets) {
    return false;
  } else {
    return internalDefinition.value.definitionParameterValueSets.length > 0;
  }
});

function editDefinition() {
  toggleEditDefinitionDialog(true);
  emit('click:edit');
}

watchEffect(() => {
  internalDefinition.value = cloneDeep(definition.value);
});

function deleteDefinitionTooltipInfo() {
  if (mode.value !== 'details') {
    if (isPredefined.value && !isNewDefinition.value)
      return t('RemovePredefinedDefinitionTooltipMsg');

    if (!isPredefined.value && !isNewDefinition.value && isReferenced.value)
      return t('RemoveDefinitionOfReferencedQuantityTooltipMsg');

    if (isReferenced.value && !isNewDefinition.value) return t('RemoveDefinitionTooltipMsg');

    return t('RemoveDefinition');
  }
}
</script>
<template>
  <v-expansion-panel>
    <v-expansion-panel-title>
      <template #default>
        <h5 class="text-h5 text-primary-darken-2 mb-0">
          <ValidationIndicator :paths="[name]">
            {{ translateEntityProperty('name', definition) }}
          </ValidationIndicator>
        </h5>
      </template>
      <template #actions="{ expanded }">
        <VSpacer />
        <div class="d-flex" @click.stop>
          <Tooltip v-if="mode !== 'details'">
            <template #target="{ props: tooltipProps }">
              <div v-bind="tooltipProps" style="cursor: pointer">
                <GhostVBtn
                  class="mr-3"
                  :icon="editIcon"
                  size="small"
                  color="secondary"
                  :disabled="isPredefined"
                  :title="$t('EditDefinition')"
                  @click="editDefinition"
                />
              </div>
            </template>
            <template #tooltip>
              {{ isPredefined ? $t('EditPredefinedDefinitionTooltipMsg') : $t('EditDefinition') }}
            </template>
          </Tooltip>
          <Tooltip v-if="mode !== 'details'">
            <template #target="{ props: tooltipProps }">
              <div v-bind="tooltipProps" style="cursor: pointer">
                <PtwDeleteButton
                  class="mr-3"
                  :disabled="
                    hasDependingParameterSets ||
                    ((isReferenced || isPredefined) && !isNewDefinition)
                  "
                  @delete="$emit('remove')"
                />
              </div>
            </template>
            <template #tooltip>
              {{ deleteDefinitionTooltipInfo() }}
            </template>
          </Tooltip>
        </div>

        <v-icon :icon="expanded ? '$collapse' : '$expand'"></v-icon>
      </template>
    </v-expansion-panel-title>

    <v-expansion-panel-text>
      <DefinitionTable :definition="internalDefinition" />
      <div class="d-flex justify-end mt-4">
        <Dialog v-model="isEditDefinitionDialogOpen" size="large">
          <template #title>
            {{ $t('EditDefinition') }}
          </template>

          <template #text>
            <DefinitionModal
              :value="internalDefinition"
              :allowExtend="allowExtend"
              @update="
                internalDefinition = $event;
                $emit('update', $event);
                toggleEditDefinitionDialog(false);
              "
              @close="toggleEditDefinitionDialog(false)"
            />
          </template>
        </Dialog>
      </div>

      <DefinitionParameterSets
        :disabled="disabled"
        :allowExtend="allowExtend"
        :name="`${name}`"
        @update="$emit('update', $event)"
      />

      <InlineValidationError
        :name="`${name}.definitionParameterValueSets`"
        :customChildErrorMessage="$t('AtLeastOneParameterValueSetIsRequired')"
      />
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>
